// Data seam for the notification center. What happened in the
// account while you were away: handoffs first (someone is waiting), then
// mentions/assignments, then system events. Grouped by day in the page.
// Unread is carried as a flag and rendered with a dot + weight + the word
// "Nuevo" — never colour alone (accessibility rule fixed by this screen).
//
// Wired: getNotifications() -> GET /notifications, the STORED per-user feed
// (one row per recipient per dispatched event). Read state: "marcar todas como
// leídas" -> POST /notifications/read-all and each row's deep-link marks itself
// read -> POST /notifications/:id/read, both applied optimistically then
// refetched. Conversation items carry `conversationId` and deep-link to
// /inbox?c=<id>.

import { apiClient } from '../../api/client.js'

export type NotifType = 'handoff' | 'assign' | 'system'
export type NotifDay = 'hoy' | 'antes'

export interface NotifItem {
  id: string
  day: NotifDay
  type: NotifType
  title: string
  description: string
  /** Relative time, e.g. "hace 5 min", "ayer, 18:42". */
  time: string
  /** Inline action label, e.g. "Abrir conversación". Optional. */
  action?: string
  /** The conversation this points at, when any — drives the inbox deep-link. */
  conversationId?: string
  /** A fixed in-app destination for non-conversation events (billing, today). */
  link?: string
  unread: boolean
}

export interface NotificationsData {
  items: NotifItem[]
  /** Total unread across the account — accurate even when it exceeds the feed's
   *  cap (the feed itself is capped, this count is not). */
  unreadCount: number
}

/**
 * GET /notifications — the stored per-user feed (one row per dispatched event).
 * The item list is capped; `unreadCount` stays accurate beyond the cap.
 */
export async function getNotifications(): Promise<NotificationsData> {
  const res = await apiClient.get<{ data: NotificationsData }>('/notifications')
  return res.data.data
}

/** Mark every notification as read. */
export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.post('/notifications/read-all', {})
}

/** Mark a single notification as read. */
export async function markNotificationRead(id: string): Promise<void> {
  await apiClient.post(`/notifications/${encodeURIComponent(id)}/read`, {})
}

/** The conversation a notification points at (handoff / assignment), or null
 *  for non-conversation items. The API sets `conversationId` from the stored
 *  notification's resource. */
export function notifConversationId(item: NotifItem): string | null {
  return item.conversationId ?? null
}
